import { canTrackInBrowser, getCheckoutMarketingData, getMarketingConsent, publicTrackingPath, setMarketingRevoker, setMarketingTransport, type BrowserMarketingEvent } from "./marketing-runtime";
import { gtmIdSchema, pixelIdSchema, type TrackingSettings } from "./marketing-api";

type PixelFunction = ((...args: unknown[]) => void) & { queue: unknown[][]; loaded?: boolean; version?: string; disablePushState?: boolean; callMethod?: (...args: unknown[]) => void; push?: PixelFunction };
type MarketingWindow = Window & typeof globalThis & { fbq?: PixelFunction; _fbq?: PixelFunction; dataLayer?: unknown[] };
function googleConsent(target: MarketingWindow, mode: "default" | "update", state: "granted" | "denied"): void {
  target.dataLayer ??= [];
  function command(): void {
    // eslint-disable-next-line prefer-rest-params -- Google processes native Arguments objects as consent commands.
    target.dataLayer?.push(arguments);
  }
  Reflect.apply(command, undefined, ["consent", mode, { analytics_storage: state, ad_storage: state, ad_user_data: state, ad_personalization: state }]);
}
export function loadMarketingScripts(settings: TrackingSettings): boolean {
  if (!settings.enabled) return false;
  const pixelId = pixelIdSchema.safeParse(settings.meta_pixel_id).success ? settings.meta_pixel_id : null;
  const gtmId = getMarketingConsent() === "accepted" && gtmIdSchema.safeParse(settings.gtm_container_id).success ? settings.gtm_container_id : null;
  if (!pixelId && !gtmId) return false;
  const current = new URL(window.location.href);
  if (publicTrackingPath(current.pathname) && !current.pathname.startsWith("/pedido/")) {
    getCheckoutMarketingData();
    for (const name of ["fbclid", "gclid", "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "utm_id", "src", "sck"]) if (current.searchParams.has(name)) current.searchParams.delete(name);
    if (current.href !== window.location.href) window.history.replaceState(window.history.state, "", current.pathname + current.search + current.hash);
  }
  if (!canTrackInBrowser()) return false;
  const target = window as MarketingWindow;
  setMarketingRevoker(stopConsentedMarketingScripts);
  if (pixelId) {
    if (!target.fbq) {
      const pixel = ((...args: unknown[]) => {
        if (pixel.callMethod) pixel.callMethod(...args);
        else pixel.queue.push(args);
      }) as PixelFunction;
      pixel.queue = []; pixel.loaded = true; pixel.version = "2.0"; pixel.push = pixel;
      target.fbq = pixel; target._fbq = pixel;
    }
    target.fbq.disablePushState = true;
    let script = document.getElementById("alfabetizando-meta-pixel") as HTMLScriptElement | null;
    const initializedIds = (script?.dataset.pixelIds ?? "").split(",").filter(Boolean);
    if (!initializedIds.includes(pixelId)) {
      target.fbq("set", "autoConfig", false, pixelId);
      target.fbq("init", pixelId);
      initializedIds.push(pixelId);
    }
    if (!script) {
      script = document.createElement("script");
      script.id = "alfabetizando-meta-pixel"; script.async = true; script.referrerPolicy = "no-referrer";
      script.src = "https://connect.facebook.net/en_US/fbevents.js";
      document.head.append(script);
    }
    script.dataset.pixelIds = initializedIds.join(",");
  }
  if (gtmId && !document.getElementById("alfabetizando-gtm")) {
    target.dataLayer ??= [];
    googleConsent(target, "default", "granted");
    target.dataLayer.push({ "gtm.start": Date.now(), event: "gtm.js" });
    const script = document.createElement("script");
    script.id = "alfabetizando-gtm"; script.async = true; script.referrerPolicy = "no-referrer";
    script.src = "https://www.googletagmanager.com/gtm.js?id=" + gtmId;
    document.head.append(script);
  }
  if (gtmId) googleConsent(target, "update", "granted");
  setMarketingTransport((event: BrowserMarketingEvent) => {
    if (!canTrackInBrowser()) return;
    const source = window.location.origin + String(event.params.page_path ?? "/");
    if (pixelId && target.fbq) {
      target.fbq(event.event === "PaymentGenerated" ? "trackSingleCustom" : "trackSingle", pixelId, event.event,
        { ...event.params, event_source_url: source }, { eventID: event.event_id });
    }
    if (gtmId && getMarketingConsent() === "accepted") {
      target.dataLayer ??= [];
      target.dataLayer.push({ event: event.event, event_id: event.event_id, ...event.params, page_location: source, page_title: "O Novo Professor" });
    }
  });
  return Boolean(pixelId || gtmId);
}
function stopConsentedMarketingScripts(): void {
  const target = window as MarketingWindow;
  if (target.dataLayer) googleConsent(target, "update", "denied");
}
export function stopMarketingScripts(): void {
  setMarketingTransport(null);
  stopConsentedMarketingScripts();
}
export function reloadMarketingDocument(): void { window.location.reload(); }
